import React from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const backgroundShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Styled Components
const Container = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(-45deg, #e0f2f1, #b2dfdb, #e0f2f1);
  background-size: 400% 400%;
  animation: ${backgroundShift} 20s ease infinite;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1rem;
  color: #004d40;
  animation: ${slideInUp} 1s ease-out;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #00695c;
  max-width: 800px;
  animation: ${slideInUp} 1s ease-out 0.2s both;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  animation: ${slideInUp} 0.8s ease-out ${props => props.delay || '0s'} both;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    animation: ${float} 2s ease-in-out infinite;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.color || '#00796b'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 2rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: #004d40;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #4db6ac;
  line-height: 1.6;
`;

const CTAButton = styled.button`
  margin-top: 3rem;
  padding: 1rem 2.5rem;
  background: linear-gradient(45deg, #00796b, #004d40);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${slideInUp} 1s ease-out 1s both;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 121, 107, 0.3);
    background: linear-gradient(45deg, #00695c, #004d40);
  }
`;

// React Component
const DigitalMarketingServices = () => {
  const services = [
    {
      title: "Expand Online Presence",
      description: "Partnering with SRK Softwares extends your digital reach. Our IT solutions empower you to connect with a broader audience, enhancing your online footprint and driving growth in the digital world. Start your brand's visibility and impact.",
      icon: "🌐",
      color: "#00796b",
      delay: "0.3s"
    },
    {
      title: "Know Your Audience",
      description: "At SRK Softwares, we make sure your message reaches the right people. With our IT solutions, connecting with your target audience becomes easy, leading to genuine and lasting digital success.",
      icon: "👥",
      color: "#d32f2f",
      delay: "0.4s"
    },
    {
      title: "Profitable Branding",
      description: "Experience cost-effective branding excellence with Us. Our IT services empower your brand, optimizing resources while delivering top-tier solutions for a strong and budget-wise brand identity.",
      icon: "💰",
      color: "#388e3c",
      delay: "0.5s"
    },
    {
      title: "New Opportunities",
      description: "SRK Softwares helps you bring back interested customers. With their IT expertise, you can boost sales and build strong customer relationships.",
      icon: "🚀",
      color: "#fbc02d",
      delay: "0.6s"
    },
    {
      title: "Measurable Results",
      description: "With SRK Softwares' services, experience the advantage of easily trackable and measurable results. Our IT solutions empower you to monitor progress effectively, ensuring success with every step.",
      icon: "📊",
      color: "#6a1b9a",
      delay: "0.7s"
    },
    {
      title: "Get More Revenue",
      description: "Experience a revenue boost with our expert IT solutions, optimizing your business operations and get growth opportunities for increased profitability and success.",
      icon: "📈",
      color: "#0097a7",
      delay: "0.8s"
    }
  ];

  return (
    <Container>
      <Title>How Can Our SRK Softwares Agency Benefit your Business?</Title>
      <Subtitle>
        Explore how SRK Softwares' top-notch digital marketing services can drive growth and success for your business across India.
      </Subtitle>
      
      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard key={index} delay={service.delay}>
            <IconWrapper color={service.color}>
              {service.icon}
            </IconWrapper>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
          </ServiceCard>
        ))}
      </ServicesGrid>
      
      <CTAButton>Get Started with SRK Softwares</CTAButton>
    </Container>
  );
};

export default DigitalMarketingServices;